/**
 * AuditLogDetailModal
 *
 * Mirrors the redesigned OrderDetailModal pattern (hero header + accent-bar
 * section cards). Renders an audit row with:
 *   - Actor (live + frozen denormalized identity for deleted users)
 *   - Target (or "system event")
 *   - Action context (category + success/failure)
 *   - Details (AuditDiffView for diff-bearing actions; pretty JSON otherwise)
 *   - Request metadata (IP, user agent, requestId with "view all events"
 *     link to render the cascade of rows from the same user action)
 *   - Integrity (this row's hash + prevHash)
 */

import React, { useEffect, useState } from 'react';
import {
  FiUser, FiTarget, FiActivity, FiInfo, FiClock, FiShield, FiX,
  FiCheckCircle, FiAlertCircle, FiHash, FiGlobe, FiCpu,
  FiAlertTriangle, FiCode, FiCopy, FiCheck,
} from 'react-icons/fi';
import AuditDiffView from './AuditDiffView';
import { auditLogsAPI, errorLogsAPI } from '../../services/api';
import Button from '../common/Button';

function SectionCard({ icon, title, children, accent = '#145f5f' }) {
  return (
    <div
      className="bg-white rounded-[12px] border overflow-hidden"
      style={{ borderColor: '#e8e2d6', boxShadow: '0 1px 3px rgba(8,17,31,.04)' }}
    >
      <div
        className="px-4 py-2.5 border-b flex items-center gap-2"
        style={{ borderColor: '#e8e2d6', background: 'linear-gradient(180deg, #fdfbf6 0%, #ffffff 100%)' }}
      >
        <span className="w-[3px] h-3.5 rounded-full shrink-0" style={{ background: accent }} />
        {icon && <span className="text-[12px]" style={{ color: accent }}>{icon}</span>}
        <span className="text-[11.5px] font-bold text-[#08111f] tracking-tight">{title}</span>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

// Small inline copy-to-clipboard button. Used by InfoRow (when `copyable` is
// set) and by HashCell. Same visual treatment everywhere so the admin learns
// the affordance once — green "Copied" pill on success, plain pill otherwise.
function CopyChip({ value, title = 'Copy to clipboard' }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_) {
      // Older browsers / permission denied — passive no-op. The value text
      // next to us stays selectable so the admin can still copy by hand.
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title={title}
      className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-[5px] text-[9.5px] font-semibold transition-colors shrink-0"
      style={{ background: copied ? '#e8efe5' : '#f8f5ef', color: copied ? '#4a6b3f' : '#7a7060' }}
    >
      {copied ? <FiCheck className="text-[10px]" /> : <FiCopy className="text-[10px]" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function InfoRow({ icon, label, value, mono = false, copyable = false, copyValue }) {
  if (value === undefined || value === null || value === '') return null;
  // `copyValue` lets callers override what gets written to the clipboard
  // (useful when the displayed value is a React node but the underlying
  // string is plain). Falls back to the displayed value when it's a string.
  const clipboardValue = copyValue ?? (typeof value === 'string' || typeof value === 'number' ? String(value) : null);
  const showCopy = copyable && clipboardValue;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">
        {icon && <span className="text-[10px] text-[#145f5f]">{icon}</span>}
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
        <span
          className={`text-[12px] text-[#08111f] break-words ${mono ? '' : 'font-medium'}`}
          style={mono ? { fontFamily: "'JetBrains Mono', monospace" } : undefined}
        >
          {value}
        </span>
        {showCopy && <CopyChip value={clipboardValue} />}
      </div>
    </div>
  );
}

function categoryChip(cat, success) {
  const palettes = {
    auth:           { bg: success === false ? '#f2e9e6' : '#e3f0ed', color: success === false ? '#9a564a' : '#145f5f', icon: <FiUser /> },
    'admin.user':   { bg: '#f3ebd7', color: '#836016', icon: <FiShield /> },
    'admin.role':   { bg: '#edeef3', color: '#3a4a66', icon: <FiShield /> },
    'admin.ai_model': { bg: '#f3ebd7', color: '#836016', icon: <FiCpu /> },
    'admin.service':  { bg: '#edeef3', color: '#3a4a66', icon: <FiActivity /> },
    company:        { bg: '#e8efe5', color: '#4a6b3f', icon: <FiTarget /> },
    assignment:     { bg: '#f3ebd7', color: '#8a6a1f', icon: <FiShield /> },
    draft:          { bg: '#efe9e0', color: '#6f6049', icon: <FiInfo /> },
    file:           { bg: '#edeef3', color: '#3a4a66', icon: <FiInfo /> },
    system:         { bg: '#f0ebe0', color: '#7a7060', icon: <FiActivity /> },
  };
  return palettes[cat] || palettes.system;
}

function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'medium' });
  } catch (e) {
    return String(d);
  }
}

function shortHash(h) {
  if (!h) return '';
  if (h.length <= 16) return h;
  return `${h.slice(0, 8)}…${h.slice(-8)}`;
}

// Copyable hash cell. Shows the abbreviated form by default; click the value
// to expand to the full sha256, hit the Copy chip to grab it. The 8-char
// prefix is itself searchable in the admin search box (the backend matches
// 6–16 hex chars as a fingerprint prefix), so this both surfaces enough for
// visual triage and gives the admin a one-click way to grab the full hex.
//
// Uses the shared CopyChip so the affordance is identical to the one
// rendered next to User ID / Target ID / Email rows.
function HashCell({ value, label, mono = true }) {
  const [expanded, setExpanded] = useState(false);
  if (!value) return null;
  const display = expanded ? value : shortHash(value);

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">
        <span className="text-[10px] text-[#145f5f]"><FiHash /></span>
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[12px] text-[#08111f] hover:text-[#145f5f] break-all text-left"
          style={mono ? { fontFamily: "'JetBrains Mono', monospace" } : undefined}
          title={expanded ? 'Collapse' : 'Click to show full hash'}
        >
          {display}
        </button>
        <CopyChip value={value} title="Copy full hash to clipboard" />
      </div>
    </div>
  );
}

// Detect whether the row came from errorlogs vs auditlogs. errorlogs rows
// have `errorName` + `fingerprint`; auditlogs rows have `action` + `actionCategory`.
function isErrorRow(row) {
  return !!(row?.errorName || row?.fingerprint || row?.__kind === 'error' || row?.source === 'frontend' || row?.source === 'backend');
}

const ERROR_LEVEL_PALETTE = {
  critical: { bg: '#f2e9e6', color: '#9a564a', icon: <FiAlertTriangle /> },
  error:    { bg: '#f2e9e6', color: '#9a564a', icon: <FiAlertCircle /> },
  warn:     { bg: '#f3ebd7', color: '#8a6a1f', icon: <FiAlertCircle /> },
};

export default function AuditLogDetailModal({ row, onClose }) {
  const [requestSiblings, setRequestSiblings] = useState(null);
  const [siblingsBusy, setSiblingsBusy] = useState(false);
  const [relatedErrors, setRelatedErrors] = useState(null); // for audit rows: errors with same requestId
  const [relatedAudit, setRelatedAudit] = useState(null);   // for error rows: audit with same requestId

  // When the row is an error row, auto-fetch any audit events with the same
  // requestId (and vice versa) so the cross-link surfaces inline.
  useEffect(() => {
    if (!row?.requestId || row.requestId === 'no-request-id') return;
    let cancelled = false;
    if (isErrorRow(row)) {
      auditLogsAPI.getByRequest(row.requestId)
        .then((res) => { if (!cancelled) setRelatedAudit(res?.data?.rows || []); })
        .catch(() => { if (!cancelled) setRelatedAudit([]); });
    } else {
      errorLogsAPI.getByRequest(row.requestId)
        .then((res) => { if (!cancelled) setRelatedErrors(res?.data?.rows || []); })
        .catch(() => { if (!cancelled) setRelatedErrors([]); });
    }
    return () => { cancelled = true; };
  }, [row?.requestId, row?._id]);

  if (!row) return null;

  const errorRow = isErrorRow(row);
  const success = !errorRow && row.success !== false;
  const palette = errorRow
    ? (ERROR_LEVEL_PALETTE[row.level] || ERROR_LEVEL_PALETTE.error)
    : categoryChip(row.actionCategory, success);

  const fetchSiblings = async () => {
    if (!row.requestId || row.requestId === 'no-request-id') return;
    setSiblingsBusy(true);
    try {
      const res = errorRow
        ? await errorLogsAPI.getByRequest(row.requestId)
        : await auditLogsAPI.getByRequest(row.requestId);
      setRequestSiblings(res?.data?.rows || []);
    } catch (err) {
      setRequestSiblings([]);
    } finally {
      setSiblingsBusy(false);
    }
  };

  const actor = row.actor; // populated User or null
  const actorDisplayName = (actor && (actor.fullName || actor.name)) || row.actorName || null;
  const actorDisplayEmail = (actor && actor.email) || row.actorEmail || null;
  const actorDisplayRole = (actor && actor.roleName) || row.actorRole || null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: 'rgba(8,17,31,0.6)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[16px] flex flex-col mx-3 shadow-2xl overflow-hidden"
        style={{ width: 'min(920px, 96vw)', maxHeight: '92vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero header */}
        <div className="relative px-6 py-4 border-b shrink-0" style={{ borderColor: '#e8e2d6', background: 'linear-gradient(135deg, #f8f5ef 0%, #fdfbf6 55%, #ffffff 100%)' }}>
          <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-[#7a7060] hover:bg-white hover:text-[#08111f] hover:shadow-sm transition-all z-10">
            <FiX className="text-[16px]" />
          </button>
          <div className="flex items-start gap-3 pr-8">
            <div className="w-11 h-11 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: palette.bg, color: palette.color }}>
              <span className="text-[18px]">{palette.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[.18em] text-[#7a7060] font-semibold">
                {errorRow ? `${row.source || 'backend'} error` : 'Audit Entry'}
              </div>
              <h3 className="text-[20px] font-bold text-[#08111f] leading-tight truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {errorRow ? `${row.errorName || 'Error'}${row.errorMessage ? ': ' + row.errorMessage : ''}` : row.action}
              </h3>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                {errorRow ? (
                  <>
                    <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-semibold uppercase" style={{ background: palette.bg, color: palette.color }}>
                      {row.level}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-semibold capitalize" style={{ background: '#efe9e0', color: '#6f6049' }}>
                      {row.source}
                    </span>
                    {row.__groupedView && row.__groupCount && (
                      <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-semibold bg-[#08111f] text-white">
                        × {row.__groupCount} occurrences
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-semibold capitalize" style={{ background: palette.bg, color: palette.color }}>
                      {row.actionCategory}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[10px] font-semibold"
                      style={success ? { background: '#e8efe5', color: '#4a6b3f' } : { background: '#f2e9e6', color: '#9a564a' }}
                    >
                      {success ? <FiCheckCircle className="text-[10px]" /> : <FiAlertCircle className="text-[10px]" />}
                      {success ? 'Success' : 'Failure'}
                    </span>
                  </>
                )}
                <span className="text-[10.5px] text-[#7a7060]">· {fmtDate(row.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto cs-content-scroll px-6 py-5 space-y-5" style={{ background: '#fdfcf9' }}>

          {/* Cross-link banner: errors → related audit events, audit → related errors */}
          {errorRow && relatedAudit && relatedAudit.length > 0 && (
            <div className="px-3 py-2 rounded-[9px] border flex items-center gap-2 text-[12px]" style={{ background: '#e4f5f3', borderColor: '#a7d8d2', color: '#0d4848' }}>
              <FiShield className="text-[13px]" />
              <span>{relatedAudit.length} related audit event{relatedAudit.length === 1 ? '' : 's'} from the same request.</span>
              <button onClick={fetchSiblings} className="ml-auto text-[11px] font-semibold underline">View →</button>
            </div>
          )}
          {!errorRow && relatedErrors && relatedErrors.length > 0 && (
            <div className="px-3 py-2 rounded-[9px] border flex items-center gap-2 text-[12px]" style={{ background: '#f2e9e6', borderColor: '#e6d2cb', color: '#9a564a' }}>
              <FiAlertTriangle className="text-[13px]" />
              <span>{relatedErrors.length} related error{relatedErrors.length === 1 ? '' : 's'} from the same request — something broke during this action.</span>
              <button onClick={fetchSiblings} className="ml-auto text-[11px] font-semibold underline">View →</button>
            </div>
          )}

          <SectionCard icon={<FiUser />} title="Actor">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <InfoRow
                label="Name"
                value={actorDisplayName || (
                  <span className="italic text-[#9a9080]">
                    {errorRow
                      ? 'unauthenticated / unknown'
                      : row.actor
                        ? 'deleted user'
                        : 'Anonymous'}
                  </span>
                )}
              />
              <InfoRow label="Email" value={actorDisplayEmail} copyable />
              <InfoRow label="Role" value={actorDisplayRole} />
              <InfoRow label="User ID" value={row.actor ? String(row.actor._id || row.actor) : null} mono copyable />
            </div>
            {/* Distinguish "deleted user" (snapshot only) from "anonymous"
                (never had an actor — unauthenticated endpoint). */}
            {!actor && row.actorName && (
              <div className="text-[10px] italic text-[#7a7060]">
                Historical snapshot — user account no longer exists.
              </div>
            )}
            {!actor && !row.actorName && !errorRow && (
              <div className="text-[10px] italic text-[#7a7060]">
                Unauthenticated request — actor was anonymous by design
                (e.g. password-reset, magic-link send). Identified by IP + UA below.
              </div>
            )}
          </SectionCard>

          {/* Audit-only "Target" section. Error rows don't have a target. */}
          {!errorRow && (
            <SectionCard icon={<FiTarget />} title="Target" accent="#7a5a14">
              {row.target || row.targetName ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <InfoRow label="Name" value={row.targetName} />
                  <InfoRow label="Type" value={row.targetModel} />
                  <InfoRow label="Target ID" value={row.target ? String(row.target) : null} mono copyable />
                </div>
              ) : (
                <div className="text-[11.5px] italic text-[#7a7060]">System event — no target entity.</div>
              )}
            </SectionCard>
          )}

          {/* Error-only stack section */}
          {errorRow && (
            <SectionCard icon={<FiCode />} title="Stack trace" accent="#9a564a">
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <InfoRow icon={<FiAlertTriangle />} label="Error name" value={row.errorName} />
                  <InfoRow label="Status code" value={row.statusCode} mono />
                  <InfoRow label="Error code" value={row.errorCode} mono />
                  <HashCell label="Fingerprint" value={row.fingerprint} />
                </div>
                {row.fingerprint && (
                  <div className="text-[10px] italic text-[#7a7060]">
                    Same fingerprint = same bug. Paste the abbreviated prefix into the search box on the Audit & Error Logs page to find every other occurrence.
                  </div>
                )}
                {row.errorMessage && (
                  <div>
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">Message</div>
                    <div className="text-[11.5px] text-[#08111f] bg-white border rounded-[6px] px-2.5 py-1.5 break-words" style={{ borderColor: '#e8e2d6' }}>{row.errorMessage}</div>
                  </div>
                )}
                {row.stack && (
                  <div>
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">Stack</div>
                    <pre className="text-[10.5px] leading-snug border rounded-[6px] p-3 overflow-x-auto max-h-[280px]" style={{ background: '#f8f2f0', borderColor: '#e6d2cb', fontFamily: "'JetBrains Mono', monospace" }}>{row.stack}</pre>
                  </div>
                )}
                {row.context?.componentStack && (
                  <div>
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">React component stack</div>
                    <pre className="text-[10.5px] leading-snug bg-[#f8f5ef] border rounded-[6px] p-3 overflow-x-auto max-h-[200px]" style={{ borderColor: '#e8e2d6', fontFamily: "'JetBrains Mono', monospace" }}>{row.context.componentStack}</pre>
                  </div>
                )}
              </div>
            </SectionCard>
          )}

          {/* Audit-only Details section (diff + free-form context).
              For error rows we render Request Body + Context separately below. */}
          {!errorRow && (
            <SectionCard icon={<FiActivity />} title="Details" accent="#3a4a66">
              {row.details?.diff && Object.keys(row.details.diff || {}).length > 0 ? (
                <>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#7a7060] mb-2">Field-level changes</div>
                  <AuditDiffView delta={row.details.diff} />
                  {(() => {
                    const { diff, ...rest } = row.details || {};
                    if (rest && Object.keys(rest).length > 0) {
                      return (
                        <div className="mt-3">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-[#7a7060] mb-1.5">Additional context</div>
                          <pre className="text-[10.5px] leading-snug bg-[#f8f5ef] rounded-[8px] p-3 border overflow-x-auto" style={{ borderColor: '#e8e2d6', fontFamily: "'JetBrains Mono', monospace" }}>
                            {JSON.stringify(rest, null, 2)}
                          </pre>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              ) : (
                <pre className="text-[10.5px] leading-snug bg-[#f8f5ef] rounded-[8px] p-3 border overflow-x-auto" style={{ borderColor: '#e8e2d6', fontFamily: "'JetBrains Mono', monospace" }}>
                  {JSON.stringify(row.details || {}, null, 2)}
                </pre>
              )}
            </SectionCard>
          )}

          {/* Error-only Request Body + Context */}
          {errorRow && (
            <SectionCard icon={<FiActivity />} title="Request body & context" accent="#9a564a">
              {row.body && (
                <div>
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">Request body (sensitive fields redacted)</div>
                  <pre className="text-[10.5px] leading-snug bg-[#f8f5ef] rounded-[8px] p-3 border overflow-x-auto max-h-[200px]" style={{ borderColor: '#e8e2d6', fontFamily: "'JetBrains Mono', monospace" }}>
                    {JSON.stringify(row.body, null, 2)}
                  </pre>
                </div>
              )}
              {row.query && Object.keys(row.query || {}).length > 0 && (
                <div className="mt-2">
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">Query string</div>
                  <pre className="text-[10.5px] leading-snug bg-[#f8f5ef] rounded-[8px] p-3 border overflow-x-auto" style={{ borderColor: '#e8e2d6', fontFamily: "'JetBrains Mono', monospace" }}>
                    {JSON.stringify(row.query, null, 2)}
                  </pre>
                </div>
              )}
              {row.context && Object.keys(row.context || {}).length > 0 && (
                <div className="mt-2">
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">Context</div>
                  <pre className="text-[10.5px] leading-snug bg-[#f8f5ef] rounded-[8px] p-3 border overflow-x-auto" style={{ borderColor: '#e8e2d6', fontFamily: "'JetBrains Mono', monospace" }}>
                    {JSON.stringify(row.context, null, 2)}
                  </pre>
                </div>
              )}
              {!row.body && !row.query && !row.context && (
                <div className="text-[11.5px] italic text-[#7a7060]">No additional request context captured.</div>
              )}
            </SectionCard>
          )}

          <SectionCard icon={<FiClock />} title="Request Metadata">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <InfoRow icon={<FiGlobe />} label="IP Address" value={row.ipAddress} mono copyable />
              <InfoRow icon={<FiClock />} label="Timestamp" value={fmtDate(row.createdAt)} />
              <div className="col-span-2 min-w-0">
                <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">
                  <span className="text-[10px] text-[#145f5f]"><FiHash /></span>
                  <span>Request ID</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] text-[#08111f] break-all" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{row.requestId || 'no-request-id'}</span>
                  {row.requestId && row.requestId !== 'no-request-id' && (
                    <>
                      <CopyChip value={row.requestId} title="Copy request id to clipboard" />
                      <Button
                        variant="soft"
                        size="xs"
                        onClick={fetchSiblings}
                        loading={siblingsBusy}
                        className="!rounded-full"
                      >
                        View all events from this request →
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="col-span-2 min-w-0">
                <InfoRow label="User Agent" value={row.userAgent} mono />
              </div>
            </div>

            {requestSiblings && (
              <div className="mt-2 border-t pt-3" style={{ borderColor: '#e8e2d6' }}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#7a7060] mb-2">
                  {requestSiblings.length} event{requestSiblings.length === 1 ? '' : 's'} in this request
                </div>
                <ol className="space-y-1.5">
                  {requestSiblings.map((sib) => (
                    <li key={sib._id} className={`flex items-start gap-2 text-[11px] ${String(sib._id) === String(row._id) ? 'font-semibold' : ''}`}>
                      <span className="text-[#7a7060] tabular-nums">{fmtDate(sib.createdAt)}</span>
                      <span className="text-[#08111f]">{sib.action}</span>
                      {sib.targetName && <span className="text-[#7a7060]">— {sib.targetName}</span>}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </SectionCard>

          {/* Audit-only Integrity (hash chain). Errors don't have a hash chain. */}
          {!errorRow && (
            <SectionCard icon={<FiHash />} title="Integrity" accent="#065f46">
              <div className="grid grid-cols-1 gap-3">
                <HashCell label="This row's hash" value={row.hash} />
                {row.prevHash
                  ? <HashCell label="Previous row's hash" value={row.prevHash} />
                  : (
                    <InfoRow label="Previous row's hash" value={<span className="italic text-[#9a9080]">genesis row</span>} />
                  )}
              </div>
              <div className="text-[10px] italic text-[#7a7060] mt-2">
                Hash chain protects against post-hoc edits via the API. Run the "Verify Integrity" action on the Audit Logs page to walk the full chain.
              </div>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}
